// React bindings for the store, device prefs, sync state and the clock.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { ageNow, type Age } from '../domain/age.ts';
import { mealPlan, type MealPlan } from '../domain/feeding.ts';
import { sleepStatus, toiletStatus, type SleepStatus, type ToiletStatus } from '../domain/status.ts';
import type { CollectionName, Collections, Household, LogEvent, Profile } from '../domain/types.ts';
import { devicePrefs, subscribeDevice, type DevicePrefs } from './device.ts';
import { data, onSyncEngineChange } from './index.ts';
import { DEFAULT_HOUSEHOLD, DEFAULT_PROFILE } from './seed.ts';
import type { SyncState } from './sync.ts';

export function useStoreVersion(): number {
  const { store } = data();
  return useSyncExternalStore(store.subscribe, () => store.version);
}

export function useCollection<C extends CollectionName>(col: C): Collections[C][] {
  const { store } = data();
  return useSyncExternalStore(store.subscribe, () => store.list(col));
}

export function useDoc<C extends CollectionName>(col: C, id: string): Collections[C] | undefined {
  const { store } = data();
  return useSyncExternalStore(store.subscribe, () => store.get(col, id));
}

export function useProfile(): Profile {
  const p = useDoc('profile', 'arlo');
  return useMemo(() => ({ ...DEFAULT_PROFILE, ...p, food: { ...DEFAULT_PROFILE.food, ...p?.food } }), [p]);
}

export function useHousehold(): Household {
  const h = useDoc('settings', 'household');
  return useMemo(() => ({ ...DEFAULT_HOUSEHOLD, ...h }), [h]);
}

/** All events, newest first. */
export function useEvents(): LogEvent[] {
  const events = useCollection('events');
  return useMemo(() => [...events].sort((a, b) => b.at - a.at), [events]);
}

export function useDevice(): DevicePrefs {
  return useSyncExternalStore(subscribeDevice, devicePrefs);
}

export function useSyncState(): SyncState {
  const [engine, setEngine] = useState(() => data().sync);
  useEffect(() => onSyncEngineChange(() => setEngine(data().sync)), []);
  return useSyncExternalStore(engine.subscribe, () => engine.status);
}

/** Current time, re-rendering every `ms` (aligned to the interval). */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(
      () => {
        setNow(Date.now());
        interval = setInterval(() => setNow(Date.now()), ms);
      },
      ms - (Date.now() % ms),
    );
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [ms]);
  return now;
}

export interface LiveStatus {
  now: number;
  age?: Age;
  weeks: number;
  sleep: SleepStatus;
  toilet: ToiletStatus;
  meals: MealPlan;
  isHome: boolean;
}

/** Everything the dashboard needs, recomputed when data or the clock changes. */
export function useLiveStatus(tickMs = 30_000): LiveStatus {
  const tick = useNow(tickMs);
  const profile = useProfile();
  const household = useHousehold();
  const events = useEvents();
  return useMemo(() => {
    // The tick can be up to tickMs old: when data changes (he just woke), use the real time —
    // otherwise "woke up" a few seconds ahead of a stale clock reads as not yet happened.
    const now = Math.max(tick, Date.now());
    const age = ageNow(profile, now);
    const weeks = age?.weeks ?? 9;
    const sleep = sleepStatus(events, weeks, now);
    const toilet = toiletStatus(events, weeks, now, sleep, household.home?.type === 'flat' ? (household.home.outsideMins ?? 3) : 0);
    const meals = mealPlan({ ageWeeks: weeks, food: profile.food, household, events, now });
    const isHome = !profile.homeDate || new Date(profile.homeDate + 'T00:00').getTime() <= now;
    return { now, age, weeks, sleep, toilet, meals, isHome };
  }, [tick, profile, household, events]);
}
